import {helpdeskAxios} from '@/plugins/helpdeskAxios';
import {settings} from '@/plugins/settings';
import {IHelpdeskTicket} from '@/store/modules/helpdeskTicketsModule';

interface IThreadMessage {
  from_email?: string | null;
  to_email?: string | null;
  cc_emails?: string[] | null;
  bcc_emails?: string[] | null;
}

function collect(target: string[], m: IThreadMessage) {
  target.push(m.from_email || '', m.to_email || '');
  (m.cc_emails || []).forEach((e) => target.push(e));
  (m.bcc_emails || []).forEach((e) => target.push(e));
}

function unique(emails: string[]): string[] {
  const seen: {[k: string]: boolean} = {};
  const result: string[] = [];
  emails.forEach((raw) => {
    const email = (raw || '').trim();
    const key = email.toLowerCase();
    if (email && !seen[key]) {
      seen[key] = true;
      result.push(email);
    }
  });
  return result;
}

/**
 * Every address taking part in the ticket's conversations: reporter, the linked emails,
 * and the full Gmail thread of each one (a thread usually has more messages than the ticket links).
 */
export function ticketThreadParticipants(ticket: IHelpdeskTicket | null): Promise<string[]> {
  if (!ticket) return Promise.resolve([]);

  const emails: string[] = [ticket.reporter_email || ''];
  (ticket.emails || []).forEach((e) => collect(emails, e));
  (ticket.sent_emails || []).forEach((s) => collect(emails, s));

  // One request per distinct thread is enough: the endpoint returns the whole thread.
  const threadHeads: {[threadId: string]: number} = {};
  (ticket.emails || []).forEach((e) => {
    if (e.thread_id && !threadHeads[e.thread_id]) threadHeads[e.thread_id] = e.id;
  });

  const requests = Object.keys(threadHeads).map((threadId) =>
    helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/inbox/' + threadHeads[threadId] + '/thread', method: 'GET'})
      .then((res: any) => {
        const data = (res.data && res.data.data) ? res.data.data : {};
        (data.received || []).forEach((m: IThreadMessage) => collect(emails, m));
        (data.sent || []).forEach((m: IThreadMessage) => collect(emails, m));
      })
      // A failed thread fetch still leaves the participants we already know from the ticket.
      .catch(() => undefined),
  );

  return Promise.all(requests).then(() => unique(emails));
}
